import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { formatEur, formatPercent } from "../../lib/format-kpi";
import type { InvestmentListItem } from "../../lib/investments";

type SortMode = "pct" | "eur";

interface Entry {
  p: InvestmentListItem;
  gainEur: number;
  gainPct: number | null;
}

/**
 * Zwei Listen nebeneinander: Top 5 Gewinner + Top 5 Verlierer.
 * Ranking-Nummer, Mini-Bar fuer relative Groesse, %/€-Toggle.
 */
export function TopPositionsList({ items }: { items: InvestmentListItem[] }) {
  const { t } = useTranslation();
  const [sortMode, setSortMode] = useState<SortMode>("pct");

  const withGain: Entry[] = items
    .map((p) => {
      if (p.calc.marketValueEur === null || p.calc.purchaseValueEur === null) return null;
      const gainEur = p.calc.marketValueEur - p.calc.purchaseValueEur;
      const gainPct = p.calc.purchaseValueEur > 0 ? (gainEur / p.calc.purchaseValueEur) * 100 : null;
      return { p, gainEur, gainPct };
    })
    .filter((x): x is Entry => x !== null);

  const sortKey = (e: Entry): number =>
    sortMode === "pct" ? (e.gainPct ?? 0) : e.gainEur;

  const winners = withGain
    .filter((x) => x.gainEur > 0)
    .sort((a, b) => sortKey(b) - sortKey(a) || a.p.name.localeCompare(b.p.name))
    .slice(0, 5);

  const losers = withGain
    .filter((x) => x.gainEur < 0)
    .sort((a, b) => sortKey(a) - sortKey(b) || a.p.name.localeCompare(b.p.name))
    .slice(0, 5);

  if (winners.length === 0 && losers.length === 0) return null;

  const maxWinnerAbs = Math.max(
    ...winners.map((e) => Math.abs(sortKey(e))),
    1e-9,
  );
  const maxLoserAbs = Math.max(...losers.map((e) => Math.abs(sortKey(e))), 1e-9);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Column
        title={t("investments.top.winners_title", {
          count: 5,
          defaultValue: "Top 5 Gewinner",
        })}
        dotColor="var(--pos)"
        sortMode={sortMode}
        setSortMode={setSortMode}
        entries={winners}
        emptySlots={5 - winners.length}
        emptyLabel={t("investments.top.no_more_winners", {
          defaultValue: "— keine weiteren Gewinner —",
        })}
        maxAbs={maxWinnerAbs}
        tone="pos"
      />
      <Column
        title={t("investments.top.losers_title", {
          count: 5,
          defaultValue: "Top 5 Verlierer",
        })}
        dotColor="var(--neg)"
        sortMode={sortMode}
        setSortMode={setSortMode}
        entries={losers}
        emptySlots={5 - losers.length}
        emptyLabel={t("investments.top.no_more_losers", {
          defaultValue: "— keine weiteren Verlierer —",
        })}
        maxAbs={maxLoserAbs}
        tone="neg"
      />
    </div>
  );
}

function Column({
  title,
  dotColor,
  sortMode,
  setSortMode,
  entries,
  emptySlots,
  emptyLabel,
  maxAbs,
  tone,
}: {
  title: string;
  dotColor: string;
  sortMode: SortMode;
  setSortMode: (m: SortMode) => void;
  entries: Entry[];
  emptySlots: number;
  emptyLabel: string;
  maxAbs: number;
  tone: "pos" | "neg";
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: dotColor }}
            aria-hidden="true"
          />
          {title}
        </h3>
        <div
          className="flex gap-0.5 p-0.5 rounded-md"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <ToggleChip active={sortMode === "pct"} onClick={() => setSortMode("pct")} label="%" />
          <ToggleChip active={sortMode === "eur"} onClick={() => setSortMode("eur")} label="€" />
        </div>
      </div>
      <div className="space-y-2">
        {entries.map((e, idx) => (
          <Card key={e.p.id} rank={idx + 1} entry={e} maxAbs={maxAbs} tone={tone} sortMode={sortMode} />
        ))}
        {Array.from({ length: emptySlots }, (_, idx) => (
          <div
            key={`empty-${idx}`}
            className="rounded-lg border px-3.5 py-3 italic text-sm flex items-center"
            style={{
              borderColor: "var(--border)",
              background: "var(--card)",
              color: "var(--subtle)",
              opacity: 0.55,
            }}
          >
            <div className="text-xs font-semibold w-5">{entries.length + idx + 1}</div>
            <div className="flex-1 truncate">{emptyLabel}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({
  rank,
  entry,
  maxAbs,
  tone,
  sortMode,
}: {
  rank: number;
  entry: Entry;
  maxAbs: number;
  tone: "pos" | "neg";
  sortMode: SortMode;
}) {
  const headlineValue =
    sortMode === "pct"
      ? entry.gainPct !== null
        ? formatPercent(entry.gainPct, 2)
        : "—"
      : signedEur(entry.gainEur);
  const sortValue = sortMode === "pct" ? (entry.gainPct ?? 0) : entry.gainEur;
  const barPct = maxAbs > 0 ? Math.min(100, (Math.abs(sortValue) / maxAbs) * 100) : 0;

  const headlineColor = tone === "pos" ? "var(--pos)" : "var(--neg)";
  const barColor = tone === "pos" ? "var(--pos)" : "var(--neg)";

  return (
    <Link
      to={`/investments/${entry.p.id}`}
      className="rounded-lg border block px-3.5 py-3 transition-all hover:-translate-y-0.5"
      style={{
        background: "var(--card)",
        borderColor: "var(--border)",
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div className="flex items-center gap-3">
        <div className="text-xs font-semibold w-5 shrink-0" style={{ color: "var(--muted)" }}>
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>
            {entry.p.name}
          </div>
          <div className="text-xs num truncate mt-0.5" style={{ color: "var(--muted)" }}>
            {entry.p.calc.marketValueEur !== null ? formatEur(entry.p.calc.marketValueEur) : "—"}{" "}
            · {signedEur(entry.gainEur)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-bold num" style={{ color: headlineColor }}>
            {headlineValue}
          </div>
          <div
            className="mt-1 h-1 rounded-full overflow-hidden w-20"
            style={{ background: "var(--border-soft)" }}
          >
            <div style={{ width: `${barPct}%`, height: "100%", background: barColor }} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ToggleChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-0.5 rounded text-xs font-medium transition-colors"
      style={
        active
          ? { background: "var(--text)", color: "var(--bg)" }
          : { color: "var(--muted)", background: "transparent" }
      }
    >
      {label}
    </button>
  );
}

function signedEur(cents: number): string {
  return (cents > 0 ? "+" : "") + formatEur(cents);
}
